import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Loader2, Search } from 'lucide-react';
import { api } from '../../../lib/api';
import { TIPOS, duplicarItem } from '../../../lib/formularios';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';
import { ProductIcon } from '../../office/icons';
import { ICONOS_TIPO, detalleError } from './contexto';

/**
 * Importar preguntas de otro formulario: se elige el formulario (de los recientes de Drive) y luego
 * qué preguntas traer. Se copian con ids nuevos (`duplicarItem`), así sus respuestas no se mezclan.
 */
export default function DialogoImportar({ open, onOpenChange, formId, onImportar }) {
  const [formularios, setFormularios] = useState(null);
  const [filtro, setFiltro] = useState('');
  const [origen, setOrigen] = useState(null); // {archivo, formulario}
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [elegidos, setElegidos] = useState(new Set());

  useEffect(() => {
    if (!open) return;
    setOrigen(null);
    setElegidos(new Set());
    setError('');
    setFiltro('');
    let vivo = true;
    api.get('/drive/files', { params: { view: 'recent' } })
      .then(({ data }) => {
        if (vivo) setFormularios((data.items || []).filter((f) => f.kind === 'form' && f.id !== formId && !f.trashed));
      })
      .catch(() => vivo && setFormularios([]));
    return () => { vivo = false; };
  }, [open, formId]);

  const elegirFormulario = async (archivo) => {
    setCargando(true);
    setError('');
    try {
      const { data } = await api.get(`/forms/${archivo.id}`);
      const importables = (data.formulario?.items || []).filter((it) => it.tipo !== 'seccion');
      setOrigen({ archivo, items: importables });
      setElegidos(new Set(importables.map((it) => it.id)));
    } catch (err) {
      setError(detalleError(err, 'No se pudo abrir ese formulario'));
    } finally {
      setCargando(false);
    }
  };

  const visibles = useMemo(() => {
    const q = filtro.trim().toLowerCase();
    return (formularios || []).filter((f) => !q || (f.name || '').toLowerCase().includes(q));
  }, [formularios, filtro]);

  const importar = () => {
    const items = origen.items.filter((it) => elegidos.has(it.id)).map(duplicarItem);
    if (items.length) onImportar(items);
    onOpenChange(false);
  };

  const alternar = (id) => setElegidos((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    return n;
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="fe-dialogo max-w-lg rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {origen && (
              <button type="button" className="fe-icono-btn" onClick={() => setOrigen(null)} aria-label="Elegir otro formulario">
                <ArrowLeft />
              </button>
            )}
            Importar preguntas
          </DialogTitle>
          <DialogDescription>
            {origen ? `Preguntas de «${origen.archivo.name}»` : 'Elige el formulario del que quieres copiar preguntas.'}
          </DialogDescription>
        </DialogHeader>

        {!origen && (
          <>
            <label className="fe-buscador">
              <Search />
              <input value={filtro} onChange={(e) => setFiltro(e.target.value)} placeholder="Buscar formularios" aria-label="Buscar formularios" />
            </label>
            <div className="fe-lista-dialogo fm-scroll">
              {formularios === null || cargando ? (
                <div className="fe-centro"><Loader2 className="od-spin" /></div>
              ) : visibles.length === 0 ? (
                <p className="fe-nota">No hay otros formularios recientes en tu Drive.</p>
              ) : visibles.map((f) => (
                <button key={f.id} type="button" className="fe-fila-lista" onClick={() => elegirFormulario(f)}>
                  <ProductIcon kind="form" size={26} />
                  <span className="fe-fila-lista-texto">
                    <strong>{f.name}</strong>
                    {f.updated_at && <small>Modificado {new Date(f.updated_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}</small>}
                  </span>
                </button>
              ))}
            </div>
            {error && <p className="fe-aviso-campo">{error}</p>}
          </>
        )}

        {origen && (
          <>
            <label className="fe-check fe-check-todos">
              <input
                type="checkbox"
                checked={origen.items.length > 0 && elegidos.size === origen.items.length}
                onChange={(e) => setElegidos(e.target.checked ? new Set(origen.items.map((it) => it.id)) : new Set())}
              />
              Seleccionar todo ({origen.items.length})
            </label>
            <div className="fe-lista-dialogo fm-scroll">
              {origen.items.length === 0 && <p className="fe-nota">Ese formulario no tiene preguntas.</p>}
              {origen.items.map((it) => {
                const Icono = ICONOS_TIPO[it.tipo];
                return (
                  <label key={it.id} className="fe-fila-lista">
                    <input type="checkbox" checked={elegidos.has(it.id)} onChange={() => alternar(it.id)} />
                    {Icono && <Icono className="fe-fila-lista-icono" />}
                    <span className="fe-fila-lista-texto">
                      <strong>{it.titulo || 'Sin título'}</strong>
                      <small>{TIPOS[it.tipo]?.nombre}</small>
                    </span>
                  </label>
                );
              })}
            </div>
            <div className="fe-dialogo-pie">
              <span className="flex-1" />
              <button type="button" className="fe-btn-borde" onClick={() => onOpenChange(false)}>Cancelar</button>
              <button type="button" className="fe-btn-primario" onClick={importar} disabled={!elegidos.size}>
                Importar {elegidos.size ? `(${elegidos.size})` : ''}
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
